import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  chartConfigDispatcher,
  type TimePeriod,
  type StatTestType,
  type SignificanceLevel,
} from "@store/slices/chart-config-slice";
import type { DateGuardrails } from "@models/Dataset";
import {
  chartDrawerSelector,
  chartDrawerDispatcher,
} from "@store/slices/chart-drawer-slice";
import { questionSessionMemory } from "@/utils/question-session-memory";
import {
  useGetDatasetQuestionsResponseMutation,
  useGetDatasetsQuery,
  useGetDatasetTimeBoundsQuery,
} from "@store/api/dataset-api";
import { useDebounce } from "@hooks/use-debounce";
import {
  getTimePeriodDates,
  resolveTimePeriodInWindow,
} from "@utils/time-period-utils";

const NAMED_PERIOD_VALUES = new Set([
  "latest_quarter",
  "current_quarter",
  "previous_quarter",
  "last_quarter",
  "latest_semester",
  "current_semester",
  "previous_semester",
  "last_semester",
]);
import {
  significanceLevelToPValue,
  testTypeToApiFormat,
} from "@utils/statistical-testing-utils";
import { processBarChartData, type WeightSelection } from "./useBarChartData";

const WEIGHT_SELECTION_MAP: Record<string, WeightSelection> = {
  DISABLED: "DISABLED",
  DATASET_DEFAULT: "DATASET_DEFAULT",
  SURVEY_WEIGHT: "SURVEY_WEIGHT",
};

interface UseBarChartHandlersParams {
  fetchChartData: (
    weight: WeightSelection,
    shouldApplyFilters: boolean,
    overrideDates?: { startDate: string | null; endDate: string | null },
    overrideStatTestingEnabled?: boolean,
    overridePeriodCount?: number | null,
    preserveExistingStatSig?: boolean,
  ) => Promise<void>;
  isRefetchingData: boolean;
  setWeightsApplied: React.Dispatch<React.SetStateAction<boolean>>;
  pitGuardrails?: DateGuardrails["pointInTime"];
}

export function useBarChartHandlers({
  fetchChartData,
  isRefetchingData,
  setWeightsApplied,
  pitGuardrails,
}: UseBarChartHandlersParams) {
  const dispatch = useAppDispatch();
  const { currentConfig, datasetId, questionId, activeFilters } =
    useAppSelector(chartConfigSelector);
  const { data: datasets = [], isFetching: datasetsFetching } =
    useGetDatasetsQuery({});

  // Toggle for using dashboard date filters in preview
  // Initialize to true if dashboard date filter params exist in URL
  const [usePreviewDateFilter, setUsePreviewDateFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const dfStart = params.get("df_start");
    const dfEnd = params.get("df_end");
    return Boolean(dfStart && dfEnd);
  });
  const { data: timeBounds, isFetching: timeBoundsFetching } =
    useGetDatasetTimeBoundsQuery(datasetId as string, { skip: !datasetId });
  const dataAvailabilityCutoff =
    datasets.find((d) => d.id === datasetId)?.dataAvailabilityDate ||
    timeBounds?.maxDate;
  // True once both availability queries have settled (success or no data)
  const cutoffSettled = !datasetsFetching && !timeBoundsFetching;
  const { isDrawerOpen, editingWidgetId, pendingMemoryEntry } =
    useAppSelector(chartDrawerSelector);

  const isCreatingChart = editingWidgetId === "new-widget";

  const { closeChartDrawer } = chartDrawerDispatcher(dispatch);
  const {
    updateWeight,
    updateTimePeriod,
    updateTimePeriodDates,
    updateStatTestingEnabled,
    updateRollingAverageEnabled,
    updateRollingAverageWindowSize,
    updateChartData,
    updateColorPalette,
    updateFocusParams,
    saveCurrentConfig,
    resetToDefault,
  } = chartConfigDispatcher(dispatch);

  const [getDatasetQuestionsResponse] =
    useGetDatasetQuestionsResponseMutation();

  // Refs
  const isInitialLoad = useRef(false);
  const hasFetchedOnOpen = useRef(false);
  const isHandlingStatTestingChange = useRef(false);
  const rollingAverageRef = useRef(currentConfig.rollingAverage);
  rollingAverageRef.current = currentConfig.rollingAverage;

  // Filter toggle state
  const [localApplyFilters, setLocalApplyFilters] = useState(
    activeFilters.length > 0,
  );
  const [isTogglingFilters, setIsTogglingFilters] = useState(false);
  const isTogglingFiltersRef = useRef(false);
  const debouncedApplyFilters = useDebounce(localApplyFilters, 500);

  // Get dashboard date filters from URL if toggle is enabled
  // Resolves named time periods within the dashboard filter window
  const getDashboardDateFilters = useCallback(() => {
    if (!usePreviewDateFilter) return undefined;

    const params = new URLSearchParams(window.location.search);
    const dfStart = params.get("df_start");
    const dfEnd = params.get("df_end");

    if (!dfStart || !dfEnd) return undefined;

    // Check if current chart uses a named period that needs resolution
    const timePeriod = currentConfig.timePeriodSelection?.timePeriod;
    const isNamedPeriod = timePeriod && NAMED_PERIOD_VALUES.has(timePeriod);

    if (isNamedPeriod) {
      // Module 3: resolve named period within dashboard filter window
      const effectiveEnd =
        dataAvailabilityCutoff && dataAvailabilityCutoff < dfEnd
          ? dataAvailabilityCutoff
          : dfEnd;

      const resolved = resolveTimePeriodInWindow(
        timePeriod,
        dfStart,
        effectiveEnd,
        timeBounds?.minDate,
      );

      if (resolved) {
        // Pass the full dashboard window (not just the resolved period start) so the
        // backend can look back across previous periods for rolling-average computation.
        // Using only the resolved period's startDate as the API startDate acts as a hard
        // cutoff and prevents the backend from including prior periods in the window.
        return { startDate: dfStart, endDate: effectiveEnd };
      } else {
        // No complete period - return null dates to show empty state
        return { startDate: null, endDate: null };
      }
    }

    // For all_time or custom_period, use dashboard filter dates directly
    return { startDate: dfStart, endDate: dfEnd };
  }, [
    usePreviewDateFilter,
    currentConfig.timePeriodSelection?.timePeriod,
    dataAvailabilityCutoff,
    timeBounds?.minDate,
  ]);

  // Check if current time period is "all_time" - statistical testing should be disabled
  const isAllTimePeriod =
    currentConfig.timePeriodSelection?.timePeriod === "all_time";

  // Auto-disable Statistical Testing when switching to "all_time" period
  useEffect(() => {
    if (isAllTimePeriod && currentConfig.statisticalTesting?.enabled) {
      updateStatTestingEnabled(false);
    }
  }, [
    isAllTimePeriod,
    updateStatTestingEnabled,
    currentConfig.statisticalTesting?.enabled,
  ]);

  // Sync local filter state with active filters (only when not actively toggling)
  useEffect(() => {
    if (isTogglingFiltersRef.current) return;
    setLocalApplyFilters(activeFilters.length > 0);
  }, [activeFilters.length]);

  // Initial data fetch on drawer open (editing mode).
  // For named time periods, we wait until the availability cutoff queries have settled
  // so we can resolve the correct dates before fetching (avoids double-fetch with wrong dates).
  useEffect(() => {
    if (isDrawerOpen && !isCreatingChart && datasetId && questionId) {
      if (hasFetchedOnOpen.current) return;

      const timePeriod = currentConfig.timePeriodSelection?.timePeriod;
      const isNamedPeriod =
        timePeriod &&
        timePeriod !== "all_time" &&
        timePeriod !== "custom_period";

      // Wait for cutoff queries to settle before fetching named periods
      if (isNamedPeriod && !cutoffSettled) return;

      hasFetchedOnOpen.current = true;
      isInitialLoad.current = true;

      const shouldApplyFiltersForInitialLoad =
        activeFilters.length > 0 && localApplyFilters;

      // Resolve dates using cutoff for named periods
      let overrideDates:
        | { startDate: string | null; endDate: string | null }
        | undefined;

      // Check if dashboard date filter toggle is enabled
      const dashboardDates = getDashboardDateFilters();
      if (dashboardDates) {
        overrideDates = dashboardDates;
      } else if (isNamedPeriod && timePeriod) {
        const resolved = getTimePeriodDates(timePeriod, dataAvailabilityCutoff);
        if (resolved) {
          overrideDates = {
            startDate: resolved.startDate,
            endDate: resolved.endDate,
          };
          updateTimePeriodDates(resolved);
        }
      }

      fetchChartData(
        currentConfig.weight,
        shouldApplyFiltersForInitialLoad,
        overrideDates,
        undefined,
        undefined,
        true, // preserveExistingStatSig: keep saved stat marks on initial open
      ).then(() => {
        isInitialLoad.current = false;
      });
    } else if (!isDrawerOpen) {
      isInitialLoad.current = false;
      hasFetchedOnOpen.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, isCreatingChart, cutoffSettled]);

  // Refetch on debounced filter toggle
  useEffect(() => {
    if (!isTogglingFilters) return;

    if (datasetId && questionId) {
      const refetch = async () => {
        const dashboardDates = getDashboardDateFilters();
        await fetchChartData(
          currentConfig.weight,
          debouncedApplyFilters,
          dashboardDates,
        );
        setTimeout(() => {
          isTogglingFiltersRef.current = false;
          setIsTogglingFilters(false);
        }, 0);
      };
      refetch();
    } else {
      isTogglingFiltersRef.current = false;
      setIsTogglingFilters(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedApplyFilters]);

  // Sync dates to timePeriodSelection when custom_period is selected
  useEffect(() => {
    if (
      currentConfig.timePeriodSelection?.timePeriod === "custom_period" &&
      currentConfig.responsesMinDate &&
      currentConfig.responsesMaxDate &&
      (!currentConfig.timePeriodSelection.startDate ||
        !currentConfig.timePeriodSelection.endDate)
    ) {
      updateTimePeriodDates({
        startDate: currentConfig.responsesMinDate,
        endDate: dataAvailabilityCutoff || currentConfig.responsesMaxDate,
      });
    }
  }, [
    currentConfig.timePeriodSelection?.timePeriod,
    currentConfig.timePeriodSelection?.startDate,
    currentConfig.timePeriodSelection?.endDate,
    currentConfig.responsesMinDate,
    currentConfig.responsesMaxDate,
    dataAvailabilityCutoff,
    updateTimePeriodDates,
  ]);

  const handleFilterToggle = useCallback(() => {
    isTogglingFiltersRef.current = true;
    setIsTogglingFilters(true);
    setLocalApplyFilters((prev) => !prev);
  }, []);

  const handlePreviewDateFilterToggle = useCallback(
    async (enabled: boolean) => {
      setUsePreviewDateFilter(enabled);

      if (datasetId && questionId) {
        let overrideDates:
          | { startDate: string | null; endDate: string | null }
          | undefined;

        if (enabled) {
          const params = new URLSearchParams(window.location.search);
          const dfStart = params.get("df_start");
          const dfEnd = params.get("df_end");

          if (dfStart && dfEnd) {
            const timePeriod = currentConfig.timePeriodSelection?.timePeriod;
            const isNamedPeriod = timePeriod && NAMED_PERIOD_VALUES.has(timePeriod);

            if (isNamedPeriod) {
              const effectiveEnd =
                dataAvailabilityCutoff && dataAvailabilityCutoff < dfEnd
                  ? dataAvailabilityCutoff
                  : dfEnd;
              const resolved = resolveTimePeriodInWindow(
                timePeriod,
                dfStart,
                effectiveEnd,
                timeBounds?.minDate,
              );
              // Use the full window so rolling average can look back beyond the period start.
              overrideDates = resolved
                ? { startDate: dfStart, endDate: effectiveEnd }
                : { startDate: null, endDate: null };
            } else {
              overrideDates = { startDate: dfStart, endDate: dfEnd };
            }
          }
        }

        await fetchChartData(
          currentConfig.weight,
          debouncedApplyFilters,
          overrideDates,
        );
      }
    },
    [
      datasetId,
      questionId,
      currentConfig.weight,
      currentConfig.timePeriodSelection?.timePeriod,
      dataAvailabilityCutoff,
      timeBounds?.minDate,
      debouncedApplyFilters,
      fetchChartData,
    ],
  );

  const handleWeightChange = useCallback(
    async (newWeight: WeightSelection) => {
      updateWeight(newWeight);
      const dashboardDates = getDashboardDateFilters();
      await fetchChartData(newWeight, debouncedApplyFilters, dashboardDates);
    },
    [
      updateWeight,
      fetchChartData,
      debouncedApplyFilters,
      getDashboardDateFilters,
    ],
  );

  const handleTimePeriodChange = useCallback(
    async (newTimePeriod: TimePeriod) => {
      updateTimePeriod(newTimePeriod);

      if (
        newTimePeriod === "latest_quarter" ||
        newTimePeriod === "previous_quarter"
      ) {
        const g = pitGuardrails?.units.quarter;
        if (g) {
          updateRollingAverageEnabled(g.defaultRollingAverage !== 0);
          updateRollingAverageWindowSize(
            g.defaultRollingAverage !== 0 ? g.defaultRollingAverage : 2,
          );
        } else {
          updateRollingAverageWindowSize(2);
        }
      } else if (
        newTimePeriod === "latest_semester" ||
        newTimePeriod === "previous_semester"
      ) {
        const g = pitGuardrails?.units.semester;
        if (g) {
          updateRollingAverageEnabled(g.defaultRollingAverage !== 0);
          updateRollingAverageWindowSize(
            g.defaultRollingAverage !== 0 ? g.defaultRollingAverage : 2,
          );
        } else {
          updateRollingAverageWindowSize(2);
        }
      } else {
        updateRollingAverageWindowSize(2);
      }

      if (newTimePeriod === "custom_period") {
        // If responsesMinDate and responsesMaxDate are already available, use them
        if (currentConfig.responsesMinDate && currentConfig.responsesMaxDate) {
          updateTimePeriodDates({
            startDate: currentConfig.responsesMinDate,
            endDate: dataAvailabilityCutoff || currentConfig.responsesMaxDate,
          });
          return;
        }

        // Otherwise, fetch data first to get the min/max dates
        // The useEffect will sync the dates once responsesMinDate/responsesMaxDate are populated
        await fetchChartData(currentConfig.weight, debouncedApplyFilters);
        return;
      }

      let dates: { startDate: string | null; endDate: string | null } = {
        startDate: null,
        endDate: null,
      };

      if (newTimePeriod === "all_time") {
        updateRollingAverageEnabled(false);
        updateStatTestingEnabled(false);
      } else {
        const calculatedDates = getTimePeriodDates(
          newTimePeriod,
          dataAvailabilityCutoff,
        );
        if (calculatedDates) {
          dates = calculatedDates as any;
        }
      }

      updateTimePeriodDates(dates);

      if (datasetId && questionId) {
        if (newTimePeriod === "all_time") {
          await fetchChartData(
            currentConfig.weight,
            debouncedApplyFilters,
            { startDate: null, endDate: null },
            false, // Explicitly disable stat testing for all_time
            null,
          );
        } else {
          // Determine the new rolling average period count for the fetch.
          // rollingAverageRef still holds the old state until after re-render,
          // so derive the new value from pitGuardrails if available.
          let newPeriodCount: number | null | undefined;
          if (
            newTimePeriod === "latest_quarter" ||
            newTimePeriod === "previous_quarter"
          ) {
            const g = pitGuardrails?.units.quarter;
            newPeriodCount = g
              ? g.defaultRollingAverage !== 0
                ? g.defaultRollingAverage
                : null
              : rollingAverageRef.current?.enabled
                ? 2
                : undefined;
          } else if (
            newTimePeriod === "latest_semester" ||
            newTimePeriod === "previous_semester"
          ) {
            const g = pitGuardrails?.units.semester;
            newPeriodCount = g
              ? g.defaultRollingAverage !== 0
                ? g.defaultRollingAverage
                : null
              : rollingAverageRef.current?.enabled
                ? 2
                : undefined;
          } else {
            newPeriodCount = rollingAverageRef.current?.enabled ? 2 : undefined;
          }
          await fetchChartData(
            currentConfig.weight,
            debouncedApplyFilters,
            {
              startDate: dates.startDate || null,
              endDate: dates.endDate || null,
            },
            undefined,
            newPeriodCount,
          );
        }
      }
    },
    [
      updateTimePeriod,
      updateTimePeriodDates,
      updateRollingAverageEnabled,
      updateRollingAverageWindowSize,
      updateStatTestingEnabled,
      datasetId,
      questionId,
      currentConfig.weight,
      currentConfig.responsesMinDate,
      currentConfig.responsesMaxDate,
      debouncedApplyFilters,
      fetchChartData,
      pitGuardrails,
    ],
  );

  const handleTimePeriodDatesChange = useCallback(
    async (dates: { startDate: string | null; endDate: string | null }) => {
      if (isHandlingStatTestingChange.current) {
        updateTimePeriodDates(dates);
        return;
      }

      const currentStartDate = currentConfig.timePeriodSelection?.startDate;
      const currentEndDate = currentConfig.timePeriodSelection?.endDate;
      const datesChanged =
        currentStartDate !== dates.startDate ||
        currentEndDate !== dates.endDate;

      if (dates.startDate && dates.endDate) {
        const startDate = new Date(dates.startDate);
        const endDate = new Date(dates.endDate);
        if (endDate < startDate) {
          updateTimePeriodDates(dates);
          return;
        }
      }

      updateTimePeriodDates(dates);

      if (datasetId && questionId && datesChanged) {
        if (dates.startDate && dates.endDate) {
          await fetchChartData(
            currentConfig.weight,
            debouncedApplyFilters,
            dates,
          );
        } else if (!dates.startDate && !dates.endDate) {
          await fetchChartData(currentConfig.weight, debouncedApplyFilters, {
            startDate: null,
            endDate: null,
          });
        }
      }
    },
    [
      updateTimePeriodDates,
      datasetId,
      questionId,
      fetchChartData,
      currentConfig.weight,
      currentConfig.timePeriodSelection,
      debouncedApplyFilters,
    ],
  );

  const handleStatTestingChange = useCallback(
    async (
      updateFn: () => void,
      newValues?: {
        enabled?: boolean;
        significanceLevel?: SignificanceLevel;
        minimumSampleSize?: number;
        testType?: StatTestType;
      },
    ) => {
      if (isHandlingStatTestingChange.current) return;

      updateFn();

      if (isInitialLoad.current) return;

      isHandlingStatTestingChange.current = true;

      if (datasetId && questionId) {
        try {
          if (newValues?.enabled === false) {
            await fetchChartData(
              currentConfig.weight,
              debouncedApplyFilters,
              undefined,
              false,
            );
            return;
          }

          let startDate: string | null | undefined =
            currentConfig.timePeriodSelection?.startDate;
          let endDate: string | null | undefined =
            currentConfig.timePeriodSelection?.endDate;

          if (
            !startDate &&
            !endDate &&
            currentConfig.responsesMinDate &&
            currentConfig.responsesMaxDate
          ) {
            startDate = currentConfig.responsesMinDate;
            endDate = currentConfig.responsesMaxDate;
          }

          // If no dates available, fetch them first
          if (!startDate || !endDate) {
            updateStatTestingEnabled(false);

            const dateResult = await getDatasetQuestionsResponse({
              id: questionId,
              datasetId: datasetId,
              topN: null,
              sorting: "ORIGINAL",
              weightSelection: WEIGHT_SELECTION_MAP[currentConfig.weight],
              chartType: "BAR",
              filters:
                debouncedApplyFilters && activeFilters.length > 0
                  ? activeFilters
                  : undefined,
            });

            updateStatTestingEnabled(true);

            if (
              dateResult.data?.responsesMinDate &&
              dateResult.data?.responsesMaxDate
            ) {
              startDate = dateResult.data.responsesMinDate;
              endDate = dateResult.data.responsesMaxDate;

              updateTimePeriodDates({ startDate, endDate });
              updateChartData({
                categories: currentConfig.categories,
                series: currentConfig.series,
                responsesMinDate: startDate,
                responsesMaxDate: endDate,
              });
            } else {
              return;
            }
          }

          // Fetch with stat testing enabled
          const testType = newValues?.testType
            ? newValues.testType
            : currentConfig.statisticalTesting?.testType || "z-test";

          const result = await getDatasetQuestionsResponse({
            id: questionId,
            datasetId: datasetId,
            topN: null,
            sorting: "ORIGINAL",
            weightSelection: WEIGHT_SELECTION_MAP[currentConfig.weight],
            chartType: "BAR",
            filters:
              debouncedApplyFilters && activeFilters.length > 0
                ? activeFilters
                : undefined,
            ...(currentConfig.focusMode &&
              currentConfig.focusEntity && {
                focusMode: currentConfig.focusMode,
                focusEntity: currentConfig.focusEntity,
              }),
            startDate: startDate!,
            endDate: endDate!,
            enableStatTesting: true,
            statTestingType: testTypeToApiFormat(testType),
            pValue: significanceLevelToPValue(
              currentConfig.statisticalTesting?.significanceLevel || "90%",
            ),
            minimumSampleSize:
              currentConfig.statisticalTesting?.minimumSampleSize || 30,
            ...(rollingAverageRef.current?.enabled &&
              rollingAverageRef.current?.windowSize && {
                periodCount: rollingAverageRef.current.windowSize,
              }),
          });

          if (result.data?.data && Array.isArray(result.data.data)) {
            updateChartData({
              categories: result.data.data.map(
                (item: any) => item.variableLabel,
              ),
              series: processBarChartData(result.data.data),
              responsesMinDate:
                result.data.responsesMinDate || startDate || undefined,
              responsesMaxDate:
                result.data.responsesMaxDate || endDate || undefined,
              effectiveRollingPeriodCount:
                result.data?.effectiveRollingPeriodCount,
              rollingPeriodsWarningMessage:
                result.data?.rollingPeriodsWarningMessage,
              comparisonRollingLabel: result.data?.comparisonRollingLabel,
              missingRollingPeriodLabels:
                result.data?.missingRollingPeriodLabels,
            });

            if (startDate && endDate) {
              updateTimePeriodDates({ startDate, endDate });
            }

            updateColorPalette(currentConfig.colorPalette);

            if (result.data?.weightsApplied !== undefined) {
              setWeightsApplied(result.data.weightsApplied);
            }
          }
        } catch (error) {
          console.error("Error in handleStatTestingChange:", error);
        } finally {
          isHandlingStatTestingChange.current = false;
        }
      }
    },
    [
      datasetId,
      questionId,
      currentConfig.weight,
      currentConfig.statisticalTesting,
      currentConfig.timePeriodSelection,
      currentConfig.responsesMinDate,
      currentConfig.responsesMaxDate,
      currentConfig.categories,
      currentConfig.series,
      currentConfig.colorPalette,
      currentConfig.focusMode,
      currentConfig.focusEntity,
      debouncedApplyFilters,
      activeFilters,
      getDatasetQuestionsResponse,
      updateTimePeriodDates,
      updateStatTestingEnabled,
      fetchChartData,
      updateChartData,
      updateColorPalette,
    ],
  );

  const handleRollingAverageEnabledChange = useCallback(
    (enabled: boolean) => {
      updateRollingAverageEnabled(enabled);
      const dashboardOverride = getDashboardDateFilters();
      let dates:
        | { startDate: string | null; endDate: string | null }
        | undefined;

      if (dashboardOverride) {
        if (!dashboardOverride.startDate || !dashboardOverride.endDate) {
          dates = { startDate: null, endDate: null };
        } else if (enabled) {
          // Extend startDate back by (windowSize - 1) periods so the backend
          // can include the RA lookback quarters/semesters. Per PRD-35 the
          // lookback is allowed to reach outside the filter range.
          const windowSize = currentConfig.rollingAverage?.windowSize || 2;
          const isSemester =
            currentConfig.timePeriodSelection?.timePeriod?.includes(
              "semester",
            ) ?? false;
          const monthsBack = (windowSize - 1) * (isSemester ? 6 : 3);
          const extended = new Date(dashboardOverride.startDate);
          extended.setMonth(extended.getMonth() - monthsBack);
          dates = {
            startDate: extended.toISOString().split("T")[0],
            endDate: dashboardOverride.endDate,
          };
        } else {
          dates = {
            startDate: dashboardOverride.startDate,
            endDate: dashboardOverride.endDate,
          };
        }
      } else {
        dates =
          currentConfig.timePeriodSelection?.startDate &&
          currentConfig.timePeriodSelection?.endDate
            ? {
                startDate: currentConfig.timePeriodSelection.startDate,
                endDate: currentConfig.timePeriodSelection.endDate,
              }
            : currentConfig.responsesMinDate && currentConfig.responsesMaxDate
              ? {
                  startDate: currentConfig.responsesMinDate,
                  endDate: currentConfig.responsesMaxDate,
                }
              : undefined;
      }

      fetchChartData(
        currentConfig.weight,
        debouncedApplyFilters,
        dates,
        undefined,
        enabled ? currentConfig.rollingAverage?.windowSize || 2 : null,
      );
    },
    [
      updateRollingAverageEnabled,
      getDashboardDateFilters,
      currentConfig.timePeriodSelection,
      currentConfig.responsesMinDate,
      currentConfig.responsesMaxDate,
      currentConfig.weight,
      currentConfig.rollingAverage?.windowSize,
      debouncedApplyFilters,
      fetchChartData,
    ],
  );

  const handleRollingAverageWindowSizeChange = useCallback(
    (size: number) => {
      updateRollingAverageWindowSize(size);
      const dashboardOverride = getDashboardDateFilters();
      let dates:
        | { startDate: string | null; endDate: string | null }
        | undefined;

      if (dashboardOverride) {
        if (!dashboardOverride.startDate || !dashboardOverride.endDate) {
          dates = { startDate: null, endDate: null };
        } else {
          // Extend startDate back for the new window size.
          const isSemester =
            currentConfig.timePeriodSelection?.timePeriod?.includes(
              "semester",
            ) ?? false;
          const monthsBack = (size - 1) * (isSemester ? 6 : 3);
          const extended = new Date(dashboardOverride.startDate);
          extended.setMonth(extended.getMonth() - monthsBack);
          dates = {
            startDate: extended.toISOString().split("T")[0],
            endDate: dashboardOverride.endDate,
          };
        }
      } else {
        dates =
          currentConfig.timePeriodSelection?.startDate &&
          currentConfig.timePeriodSelection?.endDate
            ? {
                startDate: currentConfig.timePeriodSelection.startDate,
                endDate: currentConfig.timePeriodSelection.endDate,
              }
            : currentConfig.responsesMinDate && currentConfig.responsesMaxDate
              ? {
                  startDate: currentConfig.responsesMinDate,
                  endDate: currentConfig.responsesMaxDate,
                }
              : undefined;
      }

      fetchChartData(
        currentConfig.weight,
        debouncedApplyFilters,
        dates,
        undefined,
        size,
      );
    },
    [
      updateRollingAverageWindowSize,
      getDashboardDateFilters,
      currentConfig.timePeriodSelection,
      currentConfig.responsesMinDate,
      currentConfig.responsesMaxDate,
      currentConfig.weight,
      debouncedApplyFilters,
      fetchChartData,
    ],
  );

  const handleSave = useCallback(async () => {
    const wasShowingFiltered = localApplyFilters;

    if (
      wasShowingFiltered &&
      activeFilters.length > 0 &&
      datasetId &&
      questionId
    ) {
      await fetchChartData(currentConfig.weight, false);
    }

    if (pendingMemoryEntry) {
      questionSessionMemory.push(pendingMemoryEntry.dashboardId, {
        questionId: pendingMemoryEntry.questionId,
        questionType: pendingMemoryEntry.questionType,
        datasetId: pendingMemoryEntry.datasetId,
        slot: "general",
      });
    }

    saveCurrentConfig();

    setTimeout(() => {
      closeChartDrawer();
    }, 50);

    if (
      wasShowingFiltered &&
      activeFilters.length > 0 &&
      datasetId &&
      questionId
    ) {
      setTimeout(() => {
        fetchChartData(currentConfig.weight, true);
      }, 100);
    }
  }, [
    localApplyFilters,
    activeFilters,
    datasetId,
    questionId,
    currentConfig.weight,
    pendingMemoryEntry,
    fetchChartData,
    saveCurrentConfig,
    closeChartDrawer,
  ]);

  const handleDiscard = useCallback(() => {
    resetToDefault();
    closeChartDrawer();
  }, [resetToDefault, closeChartDrawer]);

  return {
    // Filter state
    localApplyFilters,
    isTogglingFilters,
    debouncedApplyFilters,
    // Preview date filter state
    usePreviewDateFilter,
    // Handlers
    handleFilterToggle,
    handlePreviewDateFilterToggle,
    handleWeightChange,
    handleTimePeriodChange,
    handleTimePeriodDatesChange,
    handleStatTestingChange,
    handleRollingAverageEnabledChange,
    handleRollingAverageWindowSizeChange,
    handleSave,
    handleDiscard,
    // Derived
    isCreatingChart,
    // Dispatch helpers (exposed for focus params)
    updateFocusParams,
  };
}
